import { useMemo } from 'react';

import classNames from 'classnames';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import { useLimitedOfferStock } from '../../../../../hooks/useLimitedOfferStock';
import { useLocale } from '../../../../../hooks/useLocale';
import { useFlag } from '../../../../../services/launchDarkly';
import type {
  BenefitLayoutV3,
  EventTriggerPopupData,
  OfferElementsProps,
  OfferV2PopupType,
} from '../../../../../stores/event-trigger/types';
import { isOfferV2PopupType } from '../../../../../utils/events';
import { formatStockText } from '../../../../../utils/limitedOffer';
import { getOfferLayoutElements, getUserEventLocalizedElement } from '../../../../../utils/localizationUtils';
import { useWidgetAdaptiveText } from '../../../hooks';

import styles from './WidgetStockCounter.module.scss';

/**
 * A Limited Offer's live stock on its lobby widget, above the timer, replaced by the sold-out label
 * once the offer's soldOutAt has passed.
 *
 * The number comes from the same `stockLeft` call the offer popup uses, so the widget and the popup
 * it opens cannot disagree. Renders nothing when the event is not a limited offer, or when ops left
 * displayCap / soldOutAt unset.
 */
export const WidgetStockCounter = observer(({ event }: { event: EventTriggerPopupData }) => {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const offerLocalizationFF = useFlag('offer_localization', false);
  const limitedOfferStock = useLimitedOfferStock(event);
  const { textRef } = useWidgetAdaptiveText({ padding: 4 });

  // Multi-package offers carry one claim button per package, each free to word its label
  // differently; the widget has a single counter, so it follows the first claim button.
  const stockText = useMemo(() => {
    const { benefit } = event;
    if (!isOfferV2PopupType(benefit)) return undefined;

    const localizedElement = getUserEventLocalizedElement<'2-3-offer', BenefitLayoutV3>({
      locale,
      benefit,
      ff: offerLocalizationFF,
    });

    return getOfferLayoutElements(localizedElement, benefit as OfferV2PopupType)
      .map(([, element]) => element as OfferElementsProps)
      .find((element) => element?.type === 'ACTION_BTN')?.additionalData?.stockText;
  }, [event, locale, offerLocalizationFF]);

  if (!limitedOfferStock) return null;

  const { stock, isSoldOut } = limitedOfferStock;

  return (
    <span
      ref={textRef}
      className={classNames(styles.text)}
      data-testid={isSoldOut ? 'widget-sold-out' : 'widget-stock-counter'}
    >
      {isSoldOut ? t('limitedOffer.soldOut') : formatStockText(stockText, stock)}
    </span>
  );
});
